import { collection, getDocs } from 'firebase/firestore';

import { FSCollections } from '@src/constants/database';
import { db } from '@src/firebase/firebase';
import { apiCallable } from '@src/util/cloud-functions/apiClient';
import { API_OPERATIONS } from '@src/util/cloud-functions/operations';
import safeExecute from '@src/util/safeExecute';

export interface DivisionSummary {
  id: string;
  name: string;
}

interface DivisionSummariesParams {
  organizationId: string;
  divisionIds: string[];
}

interface DivisionSummariesResponse {
  divisions: DivisionSummary[];
}

class OrganizationDivisionRepository {
  /** All organization divisions; Firestore restricts collection reads to org-wide readers. */
  list(organizationId: string): Promise<DivisionSummary[]> {
    return safeExecute(
      async () => {
        if (!organizationId) return [];
        const snapshot = await getDocs(
          collection(db, FSCollections.ORGANIZATIONS, organizationId, FSCollections.DIVISIONS),
        );
        return snapshot.docs.map(division => {
          const data = division.data();
          return { id: division.id, name: typeof data.name === 'string' ? data.name : division.id };
        });
      },
      'OrganizationDivisionRepository.list',
      { organizationId },
    );
  }

  /**
   * Resolves division ids to names through the api gateway. The callable answers for divisions the
   * caller has no role on, as long as it shares at least one of the requested ids -- which is what
   * lets a division-scoped user read the full division list of a form it can already see.
   */
  summaries({ organizationId, divisionIds }: DivisionSummariesParams): Promise<DivisionSummary[]> {
    return safeExecute(async () => {
      if (!organizationId || !divisionIds.length) {
        return [];
      }

      const summariesFn = apiCallable<DivisionSummariesParams, DivisionSummariesResponse>(
        API_OPERATIONS.DIVISION_SUMMARIES,
      );
      const response = await summariesFn({ organizationId, divisionIds });
      return response.data?.divisions ?? [];
    }, 'OrganizationDivisionRepository.summaries', { organizationId });
  }
}

export default OrganizationDivisionRepository;
